import React, { useState, useEffect, useRef } from 'react';
import { FileText, Search, X } from 'lucide-react';
import { TemplatesService } from '@/services';
import { TemplateResposta } from '@/types';
import toast from 'react-hot-toast';

interface TemplateDropdownProps {
  onSelect: (conteudo: string) => void;
  onClose?: () => void;
}

export default function TemplateDropdown({
  onSelect,
  onClose,
}: TemplateDropdownProps) {
  const [templates, setTemplates] = useState<TemplateResposta[]>([]);
  const [busca, setBusca] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(true);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  // Fechar ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        onClose?.();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const loadTemplates = async () => {
    try {
      setIsLoading(true);
      const data = await TemplatesService.getTemplates();
      setTemplates(data);
    } catch (error: any) {
      console.error('Erro ao carregar templates:', error);
      toast.error('Erro ao carregar templates');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectTemplate = async (template: TemplateResposta) => {
    try {
      // Buscar o template e usar o conteúdo diretamente, sem processar variáveis
      const templateData = await TemplatesService.getTemplate(template.id);
      const conteudo = templateData.conteudo || '';
      
      onSelect(conteudo);
      setIsOpen(false);
      onClose?.();
      toast.success(`Template "${template.nome}" aplicado com sucesso!`);
    } catch (error: any) {
      console.error('Erro ao carregar template:', error);
      toast.error('Erro ao carregar template');
    }
  };

  const templatesFiltrados = templates.filter(template =>
    template.nome.toLowerCase().includes(busca.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[99]"
        onClick={() => {
          setIsOpen(false);
          onClose?.();
        }}
      />
      
      {/* Dropdown */}
      <div
        ref={dropdownRef}
        className="absolute bottom-full right-0 mb-2 w-96 bg-white/95 dark:bg-gray-800/95 backdrop-blur-md rounded-2xl shadow-strong border border-gray-200/50 dark:border-gray-700/50 z-[100] max-h-[28rem] flex flex-col overflow-hidden"
      >
            {/* Header com gradiente */}
            <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-r from-primary-50/50 to-blue-50/50 dark:from-gray-800/50 dark:to-gray-800/50 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="p-1.5 rounded-lg bg-primary-100 dark:bg-primary-900/50 text-primary-600 dark:text-primary-400">
                  <FileText className="w-4 h-4" />
                </div>
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Selecionar Template
                </span>
              </div>
              <button
                onClick={() => {
                  setIsOpen(false);
                  onClose?.();
                }}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Busca */}
            <div className="p-3 border-b border-gray-200/50 dark:border-gray-700/50">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Buscar template..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300/50 dark:border-gray-600/50 rounded-xl bg-white dark:bg-gray-700/50 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-primary-500/50 dark:focus:ring-primary-400/50 focus:border-primary-500 dark:focus:border-primary-400 transition-all"
                  autoFocus
                />
              </div>
            </div>

            {/* Lista de templates */}
            <div className="flex-1 overflow-y-auto p-2">
              {isLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-600 dark:border-primary-400 border-t-transparent mb-3"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Carregando templates...</p>
                </div>
              ) : templatesFiltrados.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <FileText className="w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    {busca ? 'Nenhum template encontrado' : 'Nenhum template disponível'}
                  </p>
                  {busca && (
                    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                      Tente buscar com outros termos
                    </p>
                  )}
                </div>
              ) : (
                <div className="space-y-1.5">
                  {templatesFiltrados.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleSelectTemplate(template)}
                      className="w-full text-left p-3 rounded-xl border border-gray-200/50 dark:border-gray-700/50 transition-all duration-200 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50/50 dark:hover:from-gray-700/30 dark:hover:to-gray-700/30 hover:border-primary-300/50 dark:hover:border-primary-600/50 hover:shadow-sm group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-primary-100/50 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 group-hover:bg-primary-200/50 dark:group-hover:bg-primary-800/50 transition-colors flex-shrink-0">
                          <FileText className="w-4 h-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
                            {template.nome}
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            {templatesFiltrados.length > 0 && (
              <div className="px-4 py-2.5 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-900/30">
                <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {templatesFiltrados.length} template{templatesFiltrados.length !== 1 ? 's' : ''} encontrado{templatesFiltrados.length !== 1 ? 's' : ''}
                </div>
              </div>
            )}
          </div>
        </>
      );
}
